// The agents an eval runs, as the console names them (TOM-533): Neo (chat),
// the Watch, and the experts behind Neo, each by the role the server stores
// its rows under (cloud TOM-536, ADR 0342 §1). An expert's run is the expert
// alone over its own cases (cloud ADR 0366). The experts' names are the
// Evals guide's (guide.ts), in the owner's words. Pure: tested in
// agents.test.ts.

import { expertName } from './guide';
import type { EvalRole } from './types';

/** One agent the eval can run, in the order the console lists them. */
export interface EvalAgent {
  role: EvalRole;
  label: string;
  /** A narrow column's name for it, beside its version. */
  short: string;
}

/** Every agent the console names, Neo and the Watch first, then the experts. */
export const EVAL_AGENTS: readonly EvalAgent[] = [
  { role: 'chat', label: 'Neo (chat)', short: 'Neo' },
  { role: 'watch', label: 'Watch', short: 'Watch' },
  // The experts' names are the guide's (guide.ts EXPERT_TEXT): one source.
  { role: 'boards', label: expertName('boards'), short: 'Boards' },
  { role: 'electrical', label: expertName('electrical'), short: 'Electrical' },
  { role: 'mechanical', label: expertName('mechanical'), short: 'Mechanical' },
  { role: 'electronics', label: expertName('electronics'), short: 'Electronics' },
  { role: 'navigation', label: expertName('navigation'), short: 'Navigator' },
];

/** An agent's name; a role the console does not know yet reads as itself, capitalized. */
export function agentLabel(role: EvalRole): string {
  const known = EVAL_AGENTS.find((a) => a.role === role);
  if (known !== undefined) return known.label;
  return role.length > 0 ? `${role[0]!.toUpperCase()}${role.slice(1).replace(/_/g, ' ')}` : role;
}

/** An agent's name in a narrow column ("Electrical"); an unknown role as agentLabel names it. */
export function agentShortLabel(role: EvalRole): string {
  return EVAL_AGENTS.find((a) => a.role === role)?.short ?? agentLabel(role);
}

/** An expert behind Neo, run alone: any role but Neo's chat and the Watch. */
export function isExpertRole(role: EvalRole): boolean {
  return role !== 'chat' && role !== 'watch';
}

/** The order the console lists a role in: the known agents first, then by name. */
function agentOrder(role: EvalRole): number {
  const i = EVAL_AGENTS.findIndex((a) => a.role === role);
  return i < 0 ? EVAL_AGENTS.length : i;
}

/** The agents a run's model rows name, each once, in the console's order. */
export function runAgents(models: ReadonlyArray<{ role: EvalRole }>): EvalRole[] {
  return [...new Set(models.map((m) => m.role))].sort(
    (a, b) => agentOrder(a) - agentOrder(b) || (a < b ? -1 : a > b ? 1 : 0)
  );
}

/** What a run records of its prompt versions: the map, and the two older columns. */
export interface EvalRunVersions {
  promptVersions: Record<string, string>;
  neoPromptVersion: string | null;
  watchPromptVersion: string | null;
}

/**
 * The version a run recorded for one agent: its map's (cloud TOM-536), else,
 * for Neo and the Watch, the column written before the map. Null when the run
 * recorded none, as a run still going does until its shards are finalized.
 */
export function agentVersion(run: EvalRunVersions, role: EvalRole): string | null {
  if (Object.hasOwn(run.promptVersions, role)) return run.promptVersions[role] ?? null;
  if (role === 'chat') return run.neoPromptVersion;
  if (role === 'watch') return run.watchPromptVersion;
  return null;
}

/**
 * Each agent of a run with its own version, in the console's order: an
 * expert's run names the expert's version, not Neo's. A run with no model row
 * yet names Neo and the Watch, as the page did before experts.
 */
export function agentVersions(
  run: EvalRunVersions,
  models: ReadonlyArray<{ role: EvalRole }>
): Array<{ role: EvalRole; label: string; short: string; version: string | null }> {
  const roles = models.length > 0 ? runAgents(models) : ['chat', 'watch'];
  return roles.map((role) => ({
    role,
    label: agentLabel(role),
    short: agentShortLabel(role),
    version: agentVersion(run, role),
  }));
}

/**
 * A run's versions as the run page reads them: its own map once finalized,
 * else, while its shards have all run one build, that build's (cloud ADR 0248
 * §7), so a run still going already names its expert's version.
 */
export function runVersionsOf(run: {
  promptVersions: Record<string, string>;
  neoPromptVersion: string | null;
  watchPromptVersion: string | null;
  shardCode: ReadonlyArray<{
    promptVersions: Record<string, string>;
    neoPromptVersion: string | null;
    watchPromptVersion: string | null;
  }>;
}): EvalRunVersions {
  const recorded =
    Object.keys(run.promptVersions).length > 0 ||
    run.neoPromptVersion !== null ||
    run.watchPromptVersion !== null;
  const only = run.shardCode.length === 1 ? run.shardCode[0]! : null;
  if (recorded || only === null) return run;
  return {
    promptVersions: only.promptVersions,
    neoPromptVersion: only.neoPromptVersion,
    watchPromptVersion: only.watchPromptVersion,
  };
}
